import { useCallback, useEffect, useRef } from 'react';
import {
  usePrintsUiStore,
  usePrintsConfigStore,
  usePrintsOrderStore,
} from '../stores';
import fetchImage from '../../_utils/fetch-image';
import PrintsBuilderPhotoAlbumDirectLoader from '../common/upload-photo-mixin/from-album-to-prints-loader';

const ALBUM_IMPORT_LOCAL_STORAGE_KEY = 'selected_albums';

// Returns the parsed album data from localStorage if it is valid and contains photos, otherwise null.
function parseAlbumLocalStorage() {
  const raw = localStorage.getItem(ALBUM_IMPORT_LOCAL_STORAGE_KEY);
  if (!raw) return null;
  let albumData;
  try {
    albumData = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!albumData || typeof albumData !== 'object' || Array.isArray(albumData))
    return null;
  const photoIds = Object.values(albumData).flatMap((album) => album.photos);

  return photoIds.length > 0 ? albumData : null;
}

// Handles importing photos selected on the My Photos page into Prints Builder via URL or localStorage.
// A wrapper around PrintsBuilderPhotoAlbumDirectLoader to make it work with React and provides a bridge to state actions.
//
// URL import path: uploadFromGalleryListenerUrl is set (server import job already running) - start polling for photos to import.
// localStorage import path: `selected_albums` in localStorage contains photo ids to import - POST to start import job, then poll.
//
// In both paths, `selected_albums` is cleared before the async operation begins.
export const useAlbumLoader = ({
  ready,
  uploadFromGalleryListenerUrl,
  uploadFromAlbumUrl,
}) => {
  // useRef freezes this at mount, before the effect clears localStorage on first render.
  // A plain variable would read false on subsequent re-renders after the key is removed.
  // parseAlbumLocalStorage validates the data so the flag is false when no import will actually run.
  const hasAlbumImportPending = useRef(
    !!uploadFromGalleryListenerUrl || parseAlbumLocalStorage() !== null,
  ).current;

  // --- STATE ---
  const uiUploadBegun = usePrintsUiStore((state) => state.uploadBegun);
  const uiUploadProgress = usePrintsUiStore((state) => state.uploadProgress);

  // --- ACTIONS ---
  const { getPrintSizeKeyForNewUpload } = usePrintsConfigStore((state) => ({
    getPrintSizeKeyForNewUpload: state.getters.getPrintSizeKeyForNewUpload,
  }));

  const {
    uploadBegun: orderUploadBegun,
    addUploadedPhoto,
    uploadFinished,
  } = usePrintsOrderStore((state) => ({
    uploadBegun: state.uploadBegun,
    addUploadedPhoto: state.addUploadedPhoto,
    uploadFinished: state.uploadFinished,
  }));

  // --- HOOKS ---
  const handleUploadBegun = useCallback(
    (total) => {
      uiUploadBegun(total);
      orderUploadBegun();
    },
    [uiUploadBegun, orderUploadBegun],
  );

  const handleUploadProgress = useCallback(
    (photo) => {
      const sizeKey = getPrintSizeKeyForNewUpload();

      fetchImage(
        photo.thumb_url,
        '',
        () => {
          addUploadedPhoto({ ...photo, uploaded: true, sizeKey });
          uiUploadProgress();
        },
        () => {
          addUploadedPhoto({
            ...photo,
            uploaded: false,
            sizeKey,
            thumb_url: null, // eslint-disable-line camelcase
          });
          uiUploadProgress();
        },
      );
    },
    [getPrintSizeKeyForNewUpload, addUploadedPhoto, uiUploadProgress],
  );

  const handleUploadDone = useCallback(() => {
    // This is called when the poller gets 'complete: true'.
    // It doesn't mean all thumbnails have been fetched.
    uploadFinished();
  }, [uploadFinished]);

  const createLoader = useCallback(
    (onBegunUploadingOverride) =>
      new PrintsBuilderPhotoAlbumDirectLoader(
        uploadFromAlbumUrl,
        onBegunUploadingOverride ?? handleUploadBegun,
        handleUploadProgress,
        handleUploadDone,
      ),
    [
      uploadFromAlbumUrl,
      handleUploadBegun,
      handleUploadProgress,
      handleUploadDone,
    ],
  );

  useEffect(() => {
    if (!ready) return;

    let loader;

    // URL path - start listening and remove localStorage to prevent duplicate imports
    if (uploadFromGalleryListenerUrl) {
      localStorage.removeItem(ALBUM_IMPORT_LOCAL_STORAGE_KEY);
      loader = createLoader();
      loader.listen(uploadFromGalleryListenerUrl);
    } else {
      // localStorage path - parse from localStorage and pass to loader
      const albumData = parseAlbumLocalStorage();
      if (!albumData) {
        localStorage.removeItem(ALBUM_IMPORT_LOCAL_STORAGE_KEY);
        return;
      }

      const photoIds = Object.values(albumData).flatMap((album) => album.photos);
      const albumIds = Object.keys(albumData);
      localStorage.removeItem(ALBUM_IMPORT_LOCAL_STORAGE_KEY);
      // Pre-signal loading immediately so the UI shows a spinner without waiting for the first poll response.
      // Pass a no-op to the loader so onBegunUploading is not called a second time.
      handleUploadBegun(photoIds.length);
      loader = createLoader(() => {});
      loader.slingshot(photoIds, albumIds);
    }

    return () => loader.cancel();
  }, [ready, uploadFromGalleryListenerUrl, uploadFromAlbumUrl, createLoader]);

  return { hasAlbumImportPending };
};
